import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { billingApi } from '@/api/billing'
import { Button } from '@/components/ui'
import { formatNIS } from '@/utils/currency'
import { formatDateTime } from '@/utils/date'
import type { BillingSnapshot } from '@/types/billing'

interface Props {
  orgId: string
}

export default function BillingSnapshotCard({ orgId }: Props) {
  const qc = useQueryClient()
  const { data: snapshot, isLoading } = useQuery({
    queryKey: ['admin-billing-snapshot', orgId],
    queryFn: () => billingApi.getSnapshot(orgId),
    staleTime: 60_000,
  })

  const refresh = useMutation({
    mutationFn: () => billingApi.recomputeSnapshot(orgId),
    onSuccess: () => {
      const keys: [string, string][] = [
        ['admin-billing-snapshot', orgId],
        ['admin-billing-charges', orgId],
        ['admin-billing-adjustments', orgId],
        ['admin-billing-storage', orgId],
        ['admin-billing-devices', orgId],
        ['admin-billing-central', orgId],
      ]
      for (const queryKey of keys) {
        qc.invalidateQueries({ queryKey })
      }
    },
    onError: (e: Error) => {
      toast.error(e.message || 'Refresh failed')
    },
  })

  const snap: BillingSnapshot | null | undefined = snapshot

  return (
    <div className="card p-6 border border-navy-border bg-[#1a1d27]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-secondary mb-1">Monthly total</p>
          {isLoading ? (
            <div className="flex items-center gap-2 text-ink-secondary">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : snap ? (
            <p className="text-3xl font-bold text-indigo-400 num">{formatNIS(snap.monthly_total_agorot)}</p>
          ) : (
            <p className="text-sm text-amber-400/90">Snapshot not yet computed. Click Refresh.</p>
          )}
          {snap?.next_billing_at && (
            <p className="text-sm text-ink-secondary mt-2">
              Next billing: <span className="text-ink-primary num">{snap.next_billing_at}</span>
            </p>
          )}
          {snap?.last_charge_at != null && (
            <p className="text-sm text-ink-secondary mt-1">
              Last charge:{' '}
              <span className="text-ink-primary">{formatDateTime(snap.last_charge_at)}</span>
              {snap.last_charge_amount_agorot != null && (
                <>
                  {' '}
                  · <span className="num">{formatNIS(snap.last_charge_amount_agorot)}</span>
                </>
              )}
              {snap.last_charge_status && (
                <>
                  {' '}
                  · <span className="capitalize">{snap.last_charge_status}</span>
                </>
              )}
            </p>
          )}
          {snap?.computed_at && (
            <p className="text-xs text-ink-muted mt-2">Computed {formatDateTime(snap.computed_at)}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            className="inline-flex items-center gap-2"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
          <span className="inline-flex" title="Available in Phase 4">
            <Button type="button" variant="primary" disabled className="opacity-50 cursor-not-allowed">
              Charge
            </Button>
          </span>
        </div>
      </div>
    </div>
  )
}
